import React, { useContext, useEffect } from "react";

import camelCaseKeys from "camelcase-keys";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import * as z from "zod";

import { Button, PageContent, PrimaryAction } from "../Atoms";
import { updateCart } from "../Cart/requests";
import { fetchCart } from "../Cart/requests";
import { Cart } from "../Cart/schema";
import { useLoadingState } from "../Loading";
import { useSession, useSessionDispatch } from "../Session/SessionContext";
import { useVenue } from "../VenueContext";
import { authorizedGet } from "../authorizedRequests";

export const PaymentSchema = z.object({
  id: z.number(),
  taxInclusive: z.string(),
});
export type Payment = z.infer<typeof PaymentSchema>;

export const CheckoutSessionSchema = z.object({
  id: z.string(),
  originatorId: z.string(),
  originatorType: z.union([
    z.literal("CustomerTab"),
    z.literal("OrderCart::Cart"),
  ]),
  payment: PaymentSchema,
});
export type CheckoutSession = z.infer<typeof CheckoutSessionSchema>;

const fetchCheckoutSession = async (
  cartUuid: string,
  uuid: string
): Promise<CheckoutSession> => {
  const response = await authorizedGet({
    url: `/v1/checkout_sessions/${uuid}`,
  });
  const responseData = camelCaseKeys(response.body.checkout_session, {
    deep: true,
  });

  return CheckoutSessionSchema.parse(responseData);
};

export const TabCheckoutSuccess = () => {
  const { cart } = useSession();
  const dispatch = useSessionDispatch();
  const navigate = useNavigate();
  const { checkoutId } = useParams();
  const { t } = useTranslation();
  const { name: venueName } = useVenue();
  const [checkoutSession, setCheckoutSession] =
    React.useState<CheckoutSession | null>();

  const loadingState = useLoadingState(async () => {
    if (!checkoutId) return;
    const checkoutSession = await fetchCheckoutSession(cart.uuid, checkoutId);
    console.log({ checkoutSession });
    setCheckoutSession(checkoutSession);
  }, []);

  const startNewTab = async () => {
    const response = await updateCart({
      uuid: cart.uuid,
      customerTabId: null,
    });
    dispatch({
      action: "updateCart",
      payload: response,
    });
    dispatch({ action: "updateCustomerTab", payload: null });
    navigate("/");
  };
  if (!checkoutSession || loadingState !== "LOADED") {
    return <div> Loading Checkout session</div>;
  }

  const primaryAction = (
    <Button onClick={startNewTab} primary>
      {t("orderConfirmed.startNewOrder")}
    </Button>
  );

  return (
    <PageContent className="space-y-32 ">
      <div className="max-w-md mx-auto md:space-y-64 ">
        <div>
          <div className="flex-1 space-y-16 text-center">
            <div className="font-semibold text-3xl">
              {t("paymentConfirmed.paymentConfirmed", { venueName })}
            </div>
            <div>{t("paymentConfirmed.farewellMessage", { venueName })}</div>
          </div>
        </div>

        <div className="flex">
          <div className="flex-1 flex flex-col min-h-256 h-full justify-between">
            <div className="hidden md:block"> {primaryAction}</div>
            <PrimaryAction>{primaryAction}</PrimaryAction>
          </div>
        </div>
      </div>
    </PageContent>
  );
};

const CheckoutSuccess = () => {
  const { cart } = useSession();
  const dispatch = useSessionDispatch();
  const navigate = useNavigate();
  const { checkoutId } = useParams();
  const { t } = useTranslation();
  const { name: venueName } = useVenue();
  const [checkoutSession, setCheckoutSession] =
    React.useState<CheckoutSession | null>();

  const [completedCart, setCompletedCart] = React.useState<Cart | null>();

  const loadingState = useLoadingState(async () => {
    if (!checkoutId) return;
    const checkoutSession = await fetchCheckoutSession(cart.uuid, checkoutId);
    setCheckoutSession(checkoutSession);
    if (checkoutSession.originatorType == "OrderCart::Cart") {
      const completedCart = await fetchCart(checkoutSession.originatorId);
      setCompletedCart(completedCart);
    }
  }, []);

  const startNewTab = async () => {
    const response = await updateCart({
      uuid: cart.uuid,
      customerTabId: null,
    });
    dispatch({
      action: "updateCart",
      payload: response,
    });
    dispatch({ action: "updateCustomerTab", payload: null });
    navigate("/");
  };
  if (!checkoutSession || loadingState !== "LOADED") {
    return <div> Loading Checkout session</div>;
  }

  const primaryAction = (
    <Button onClick={startNewTab} primary>
      {t("orderConfirmed.startNewOrder")}
    </Button>
  );

  return (
    <PageContent className="space-y-32 ">
      <div className="max-w-md mx-auto md:space-y-64 ">
        {checkoutSession.originatorType == "CustomerTab" && (
          <div>
            <div className="flex-1 space-y-16 text-center">
              <div className="font-semibold text-lg">
                {t("paymentConfirmed.paymentConfirmed")}
              </div>
              <div className="text-3xl font-bold text-center"></div>
            </div>
          </div>
        )}
        <div>
          <div className="flex-1 space-y-16 text-center">
            <div className="font-semibold text-lg">
              {t("orderConfirmed.yourOrderNumberIs")}
            </div>
            <div className="text-3xl font-bold text-center">
              {completedCart?.orderNumber}{" "}
            </div>
            <div className="text-2xl font-semibold">
              {t("orderConfirmed.orderWillBeReadySoon", { venueName })}
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="flex-1 flex flex-col min-h-256 h-full justify-between">
            <div className="hidden md:block"> {primaryAction}</div>
            <PrimaryAction>{primaryAction}</PrimaryAction>
          </div>
        </div>
      </div>
    </PageContent>
  );
};

export default CheckoutSuccess;
